import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * Cadillac Vistiq (linkId 2376, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2376.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape, following the conventions established for 2382/2377:
 * consolidate-or-split fingerprinting per entity, `autoIntelRPOCode` from
 * `brandItemCode`, real condition/isDefault/isStop threading on accessory
 * messages, model-level `hotspots` built from the default variant, and
 * `groupId`/`isDefault` derived from `removeAccessories` via connected
 * components (with single-select group categories normalised to the group's
 * dominant category — 1.0 sometimes mislabels one member).
 *
 * First Cadillac project migrated with this generator (previously
 * built by hand for 2384 CT5). `styleConfig` reuses 2384's Cadillac
 * Gothic block verbatim.
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Vistiq: {
      id: 'Vistiq',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        luxury: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5684',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5676',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5687',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '7085',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5677',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'alloys',
              sku: '5679',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        platinum: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '5684',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '5686',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '5681',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '7085',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
            {
              id: 'variants_parts',
              sku: '5683',
              projectId: '59',
              position: [0, 0, 0],
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: true,
          sku: '7085',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: false,
          sku: '7086',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '#ff1300,#0040FF,#ff9f51,#aafef4,#00b6ff,#61ff87',
        ambientLightings: {
          ambientRed: {
            isDefault: true,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              platinum: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_AMBIENT_LIGHT_GLOW_02',
              'INT_AMBIENT_LIGHT_GLOW_01',
            ],
            parentNodeName: [],
            emissiveColor: [255, 19, 0],
            emissiveIntensity: 0.5,
          },
          ambientBlue: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              platinum: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_AMBIENT_LIGHT_GLOW_02',
              'INT_AMBIENT_LIGHT_GLOW_01',
            ],
            parentNodeName: [],
            emissiveColor: [0, 64, 255],
            emissiveIntensity: 0.5,
          },
          ambientOrange: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              platinum: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_AMBIENT_LIGHT_GLOW_02',
              'INT_AMBIENT_LIGHT_GLOW_01',
            ],
            parentNodeName: [],
            emissiveColor: [255, 159, 81],
            emissiveIntensity: 0.5,
          },
          ambientCyan: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              platinum: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_AMBIENT_LIGHT_GLOW_02',
              'INT_AMBIENT_LIGHT_GLOW_01',
            ],
            parentNodeName: [],
            emissiveColor: [170, 254, 244],
            emissiveIntensity: 0.5,
          },
          ambientSkyBlue: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              platinum: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_AMBIENT_LIGHT_GLOW_02',
              'INT_AMBIENT_LIGHT_GLOW_01',
            ],
            parentNodeName: [],
            emissiveColor: [0, 182, 255],
            emissiveIntensity: 0.5,
          },
          ambientGreen: {
            isDefault: false,
            identifier: 'interior-color',
            variantIdentifiers: {
              luxury: 'interior-color',
              platinum: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_AMBIENT_LIGHT_GLOW_02',
              'INT_AMBIENT_LIGHT_GLOW_01',
            ],
            parentNodeName: [],
            emissiveColor: [97, 255, 135],
            emissiveIntensity: 0.5,
          },
        },
        relevantVariants: ['luxury', 'platinum'],
      },
      colors: {
        exterior: {
          '30ja6fs1si26': {
            sku: '5676',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          '1d0y9evbmngc': {
            sku: '5678',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          mwsv1fnhg5wz: {
            sku: '5688',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          onwrm9wz90ia: {
            sku: '5686',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
          },
          s48xyts50cye: {
            sku: '5676',
            project: '59',
            relevantVariants: [
              'platinum',
            ],
          },
          '5jdcjsfwdj7d': {
            sku: '5678',
            project: '59',
            relevantVariants: [
              'platinum',
            ],
          },
          '1xk1ykeonvpa': {
            sku: '5688',
            project: '59',
            relevantVariants: [
              'platinum',
            ],
          },
          l0xax3vaun9r: {
            sku: '5686',
            project: '59',
            relevantVariants: [
              'platinum',
            ],
          },
        },
        interior: {
          '03wk11dwf99s': {
            sku: '5687',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          j50tistnihpo: {
            sku: '5689',
            project: '59',
            relevantVariants: [
              'luxury',
            ],
            notCompatibleExteriorColors: [],
          },
          nk7jbkvai3yb: {
            sku: '5681',
            project: '59',
            relevantVariants: [
              'platinum',
            ],
            notCompatibleExteriorColors: [],
          },
          uw02zrjb3rky: {
            sku: '5685',
            project: '59',
            relevantVariants: [
              'platinum',
            ],
            notCompatibleExteriorColors: [],
          },
        },
      },
      features: {
        '50fyijsa8h88': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Headlamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Headlamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zarbo501dlfo: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1e0q63itjg52': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        x5p4553jsccd: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ljxlvsxjaks3: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_EV_charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'EV Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sdkeibj4zedx: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        wfyhlnfxcntc: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'SHADER_AKG Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'AKG Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // The AKG Speaker shader is baked at the closed-door position, so an
          // open door leaves it wrongly positioned. Close the door before playing.
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        '9v7g6wt62yu6': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Command Centre Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command Centre Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        g6h9z7ho9usu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '94q0ctfa0a8j': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ja3evostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'luxury',
          ],
          messages: [
            {
              message: 'Rear Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        domb9mqy9v1p: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_Headlamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Headlamps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9o691919xh7h': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front Grill',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9wuk2z7wdj19': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Wheels',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '7mavle88rcl3': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Sunroof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        pzig1mfltekl: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_EV_charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'EV Charger',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        d5vu1c93bze6: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_Info Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        dx9xtc3riked: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_AKG Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'AKG Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // The AKG Speaker shader is baked at the closed-door position, so an
          // open door leaves it wrongly positioned. Close the door before playing.
          affectedInteractionOnApply: {
            pdvesjvy4q3c: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        s18ecud9kcc1: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'Command Centre Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Command Centre Screen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        bslxv6wdsy1n: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'Ambient Lighting',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '5rt2ii0juuv9': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'Front Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        fu508kf9w3nt: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'Rear Seats',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        snovea3006kg: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'platinum',
          ],
          messages: [
            {
              message: 'SHADER_HUD',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'HUD',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        ljgqvrd2nc7w: {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Luxury',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'luxury',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        ibpjrajkwp10: {
          view: 'interior',
          groupId: 'platinum-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: '22in_Alloy_Platinum_Default',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '69m81c2fb2br': {
          view: 'exterior',
          groupId: 'platinum-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: '22in_Alloy_Platinum_Optional',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ALLOY',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Wheels',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8kiyf4z3dbnj': {
          view: 'interior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Variant_Parts_Platinum',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'platinum',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {},
      interactions: {
        ihmz2ch7lru2: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        '53ehrrkf0yxy': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        ee2jxibteurv: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        w3yjdzjao2i0: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        toijdriu1av5: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        g64qit5yzfp2: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'luxury',
          ],
        },
        pdvesjvy4q3c: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 5.1617,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum',
          ],
        },
        tipo90ysous9: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 5.1465,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 1.577,
                beta: 1.388,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 1.56,
                beta: 1.583,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum',
          ],
        },
        '2mbv5mgd2j0t': {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'SUNROOF_SHADE_OPEN',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_SHADE_CLOSE',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 3.2,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum',
          ],
        },
        plvdfamzavhn: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 4.3405,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum',
          ],
        },
        e6cj50layhmo: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 4.7,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum',
          ],
        },
        kheb0pgg4q7d: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'interior-color',
                'common_interior',
                'common_exterior',
                'variants_parts',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 3.9547,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'platinum',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.8,
            alpha: 2.02,
            beta: 1.47,
            radius: 5.7,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 3.2,
            upperRadiusLimit: 6.7,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_3',
            identifiers: [
              'common_exterior',
            ],
          },
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
